import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  onSelect: (pilgrim: any) => void;
  gender: 'male' | 'female';
};

export const PilgrimSelector = ({ onSelect, gender }: Props) => {
  const { translations } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [pilgrims, setPilgrims] = useState<any[]>([]);
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    const fetchPilgrims = async () => {
      setIsLoading(true);
      try {
        // Get all camp accommodation tables to check for existing assignments
        const campTablesRef = collection(firestore, 'CampAccommodationTables');
        const campTablesSnapshot = await getDocs(campTablesRef);
        const assignedPilgrimIds = new Set<string>();

        // Collect all assigned pilgrim IDs
        campTablesSnapshot.forEach(doc => {
          const table = doc.data();
          table.tents.forEach((tent: any) => {
            tent.halls.forEach((hall: any) => {
              hall.beds.forEach((bed: any) => {
                if (bed.pilgrimId) {
                  assignedPilgrimIds.add(bed.pilgrimId);
                }
              });
            });
          });
        });

        // Get pilgrims based on user role and gender
        let pilgrimsQuery;
        if (userRole === 'admin') {
          // Admin can see all pilgrims of the specified gender
          pilgrimsQuery = query(
            collection(firestore, 'Pilgrims'),
            where('Gender', '==', gender === 'male' ? 'male' : 'female')
          );
        } else {
          // Get approved links first
          const linksRef = collection(firestore, 'PilgrimGuideLinks');
          let linksQuery;
          
          if (userRole === 'guide') {
            linksQuery = query(
              linksRef,
              where('guideId', '==', userId),
              where('status', '==', 'approved')
            );
          } else { // organizer
            // Get all guides under this organizer
            const guidesRef = collection(firestore, 'Guides');
            const guidesQuery = query(guidesRef, where('organizerId', '==', userId));
            const guidesSnapshot = await getDocs(guidesQuery);
            const guideIds = guidesSnapshot.docs.map(doc => doc.id);

            if (guideIds.length === 0) {
              setPilgrims([]);
              setIsLoading(false);
              return;
            }

            linksQuery = query(
              linksRef,
              where('guideId', 'in', guideIds),
              where('status', '==', 'approved')
            );
          }

          const linksSnapshot = await getDocs(linksQuery);
          const pilgrimIds = linksSnapshot.docs.map(doc => doc.data().pilgrimId);

          if (pilgrimIds.length === 0) {
            setPilgrims([]);
            setIsLoading(false);
            return;
          }

          // Then get pilgrims that match the gender and are in the approved links
          pilgrimsQuery = query(
            collection(firestore, 'Pilgrims'),
            where('Gender', '==', gender === 'male' ? 'male' : 'female'),
            where('__name__', 'in', pilgrimIds)
          );
        }

        const snapshot = await getDocs(pilgrimsQuery);
        const pilgrimsData = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(pilgrim => !assignedPilgrimIds.has(pilgrim.id)); // Filter out already assigned pilgrims

        setPilgrims(pilgrimsData);
      } catch (error) {
        console.error('Error fetching pilgrims:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPilgrims();
  }, [gender, userRole, userId]);

  // Filter pilgrims based on search term
  const filteredPilgrims = pilgrims.filter(pilgrim => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      pilgrim.Name.toLowerCase().includes(searchLower) ||
      String(pilgrim.Passport_Number).toLowerCase().includes(searchLower) ||
      pilgrim.Residence_Country.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={
            translations?.pilgrimSelector?.searchPlaceholder ||
            'Search by pilgrim name or passport number...'
          }
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-right
                   focus:ring-2 focus:ring-primary-500 transition-all"
        />
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          size={20}
        />
      </div>

      {/* Pilgrims List */}
      <div className="border rounded-lg max-h-60 overflow-y-auto">
        {isLoading ? (
          <div className="p-4">
            <LoadingSpinner />
          </div>
        ) : filteredPilgrims.length > 0 ? (
          filteredPilgrims.map((pilgrim) => (
            <button
              key={pilgrim.id}
              onClick={() => onSelect(pilgrim)}
              className="w-full p-4 text-right hover:bg-gray-50 transition-colors border-b last:border-b-0"
            >
              <div className="font-medium">{pilgrim.Name}</div>
              <div className="text-sm text-gray-500">
                {translations?.pilgrimSelector?.passport || 'Passport'}:{' '}
                {pilgrim.Passport_Number}
              </div>
              <div className="text-sm text-gray-500">
                {pilgrim.Residence_Country}
              </div>
              <div className="text-sm text-gray-500">
                {translations?.pilgrimSelector?.package || 'Package'}:{' '}
                {pilgrim.package_nickName}
              </div>
            </button>
          ))
        ) : (
          <div className="p-4 text-center text-gray-500">
            {translations?.pilgrimSelector?.noResults ||
              'No matching pilgrims found'}
          </div>
        )}
      </div>
    </div>
  );
};